<template>
    <div class="container">
        <div class="row mb-4">
            <div class="col">
                <section class="history-filters bg-white">
                    <header class="history-filters__header d-flex align-items-start gap-3">
                        <span class="history-filters__heading-icon flex-shrink-0">
                            <BootstrapIcon name="sliders" :size="20" />
                        </span>
                        <div>
                            <h1 class="h5 fw-bolder text-gray-900 mb-1">{{$t('requisition.filter_orders')}}</h1>
                            <p class="small text-gray-500 mb-0">{{$t('requisition.filter_orders_description')}}</p>
                        </div>
                    </header>

                    <div class="history-filters__section">
                        <p class="history-filters__label small fw-bolder text-gray-600 mb-2">{{$t('requisition.filter_by_status')}}</p>
                        <div class="history-filters__statuses d-flex flex-wrap gap-2">
                            <button
                            v-for="status in statusFilters"
                            :key="status.key"
                            type="button"
                            class="history-filters__status d-inline-flex align-items-center gap-2"
                            :class="[status.className, {'history-filters__status--inactive': !filters.status[status.key]}]"
                            :aria-pressed="filters.status[status.key] ? 'true' : 'false'"
                            @click="filters.status[status.key] = !filters.status[status.key]"
                            >
                                <BootstrapIcon :color="status.iconColor" :name="filters.status[status.key] ? 'check-circle-fill' : 'circle'" :size="16" />
                                <span>{{status.label}}</span>
                            </button>
                        </div>
                    </div>

                    <div class="history-filters__bottom row g-3 align-items-end">
                        <div class="col-12 col-md-5 col-lg-4">
                            <p class="history-filters__label small fw-bolder text-gray-600 mb-2">{{$t('requisition.time_period')}}</p>
                            <div class="dropdown">
                                <button type="button" class="history-filters__period d-flex align-items-center w-100 border-0" data-bs-toggle="dropdown" aria-expanded="false">
                                    <BootstrapIcon class="history-filters__period-icon flex-shrink-0" name="calendar-range" :size="18" />
                                    <span class="flex-fill text-start fw-bolder">
                                        <span v-if="filters.from_h === 48">{{$t('requisition.last_hours', { HOURS: 48 })}}</span>
                                        <span v-else-if="filters.from_h === 168">{{$t('requisition.last_days', { DAYS: 7 })}}</span>
                                        <span v-else-if="filters.from_h === 720">{{$t('requisition.last_days', { DAYS: 30 })}}</span>
                                        <span v-else-if="filters.from_h === 2160">{{$t('requisition.last_months', { MONTHS: 3 })}}</span>
                                    </span>
                                    <BootstrapIcon class="history-filters__period-chevron flex-shrink-0" name="chevron-down" :size="14" />
                                </button>
                                <ul class="history-filters__period-menu dropdown-menu w-100">
                                    <li><button type="button" class="dropdown-item" :class="{'active': filters.from_h === 48}" @click="filters.from_h = 48">{{$t('requisition.last_hours', { HOURS: 48 })}}</button></li>
                                    <li><button type="button" class="dropdown-item" :class="{'active': filters.from_h === 168}" @click="filters.from_h = 168">{{$t('requisition.last_days', { DAYS: 7 })}}</button></li>
                                    <li><button type="button" class="dropdown-item" :class="{'active': filters.from_h === 720}" @click="filters.from_h = 720">{{$t('requisition.last_days', { DAYS: 30 })}}</button></li>
                                    <li><button type="button" class="dropdown-item" :class="{'active': filters.from_h === 2160}" @click="filters.from_h = 2160">{{$t('requisition.last_months', { MONTHS: 3 })}}</button></li>
                                </ul>
                            </div>
                        </div>
                        <div class="col-12 col-md-7 col-lg-8">
                            <p class="history-filters__label small fw-bolder text-gray-600 mb-2">{{$t('requisition.search_orders')}}</p>
                            <form class="history-filters__search" role="search" @submit="submit">
                                <label for="history-order-search" class="visually-hidden">{{$t('requisition.search_by_fields')}}</label>
                                <div class="calendar-search__control d-flex align-items-center">
                                    <BootstrapIcon class="calendar-search__icon flex-shrink-0" name="search" :size="19" />
                                    <input
                                    id="history-order-search"
                                    v-model="filters.search"
                                    type="search"
                                    class="calendar-search__input flex-fill border-0 bg-transparent"
                                    :placeholder="$t('requisition.search_by_fields')"
                                    :aria-label="$t('requisition.search_by_fields')"
                                    autocomplete="off"
                                    >
                                    <button
                                    v-if="filters.search.length > 0"
                                    type="button"
                                    class="calendar-search__clear flex-shrink-0 border-0"
                                    :aria-label="$t('requisition.clear_search')"
                                    :title="$t('requisition.clear_search')"
                                    @click="filters.search = ''"
                                    >
                                        <BootstrapIcon name="x-lg" :size="15" />
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </section>
            </div>
        </div>

        <div v-if="orders.length > 0" class="row">
            <div class="col">
                <div v-for="(order, indexO) in orders" :key="indexO" class="mt-2">
                    <OrderWidget :orderIndex="indexO.toString()" :order="order" :filters_search="filters.search" :business_details="business_details" />
                </div>
            </div>
        </div>
        <div v-else class="row">
            <div class="col">
                <EmptyResponse :description="$t('requisition.no_orders_found')" />
            </div>
        </div>

        <ModalConfirmEmailReceipt :order="selected_order" />
        <ModalConfirmDiscardOrder :order="selected_order" />
    </div>
</template>

<script>
import OrderWidget from '@/common/directive/OrderWidget.vue'
import BootstrapIcon from '@/common/directive/BootstrapIcon'
import InventoryOrderStore from '@/common/store/inventory_order.js'
import RequisitionStore from '@/common/store/requisition.js'
import ModalConfirmEmailReceipt from '@/common/modal/modal-confirm_email_receipt.vue'
import ModalConfirmDiscardOrder from '@/common/modal/modal-confirm_discard_order.vue'
import EmptyResponse from '@/common/directive/EmptyResponse.vue'

export default {
    components: {
        OrderWidget,
        EmptyResponse,
        BootstrapIcon,
        ModalConfirmDiscardOrder,
        ModalConfirmEmailReceipt
    },
    data: function () {
        return {
            orders: [],
            selected_order: null,
            business_details: null,
            timeout: 0,
            loading: false,
            filters: {
                search: '',
                status: {
                    order_pending: true,
                    order_preparing: true,
                    order_completed: true,
                    order_discarded: true,
                    order_delivered: true
                },
                from_h: 48
            }
        }
    },
    computed: {
        location: function () {
            return RequisitionStore.state.location
        },
        statusFilters: function () {
            return [
                { key: 'order_pending', label: this.$t('delivery.pending'), className: 'history-filters__status--pending', iconColor: 'fill-coral-500 text-coral-500' },
                { key: 'order_preparing', label: this.$t('delivery.preparing'), className: 'history-filters__status--preparing', iconColor: 'fill-teal-500 text-teal-500' },
                { key: 'order_completed', label: this.$t('delivery.completed'), className: 'history-filters__status--completed', iconColor: 'fill-cyan-500 text-cyan-500' },
                { key: 'order_discarded', label: this.$t('delivery.discarded'), className: 'history-filters__status--discarded', iconColor: 'fill-orange-400 text-orange-400' },
                { key: 'order_delivered', label: this.$t('delivery.handed'), className: 'history-filters__status--delivered', iconColor: 'fill-brown-500 text-brown-500' }
            ]
        }
    },
    watch: {
        filters: {
            deep: true,
            handler: function () {
                clearTimeout(this.timeout)

                const self = this
                this.timeout = setTimeout(async function () {
                    await self.get_history()
                }, 250)
            }
        },
        location: async function () {
            await this.get_history()
        }
    },
    created: async function () {
        const self = this

        if (this.location && this.location.id) {
            await this.get_history()
        }

        this.$bus.$off('Order/email_receipt')
        this.$bus.$on('Order/email_receipt', function (order) {
            self.selected_order = order
            self.$bus.$emit('Modal/show/confirm-email-receipt')
        })
        this.$bus.$off('Order/discard_order')
        this.$bus.$on('Order/discard_order', function (order) {
            self.selected_order = order
            self.$bus.$emit('Modal/show/confirm-order-discard')
        })

        this.$bus.$off('Order/status_handed')
        this.$bus.$on('Order/status_handed', function () {
            self.get_history()
        })
        this.$bus.$off('Order/status_discarded')
        this.$bus.$on('Order/status_discarded', function () {
            self.get_history()
        })
    },
    methods: {
        submit: async function ($event) {
            $event.preventDefault()
        },
        get_history: async function () {
            const self = this
            const query = {
                location_id: this.location.id,
                from_h: this.filters.from_h,
                status: Object.keys(this.filters.status).filter(function (key) {
                    return self.filters.status[key]
                })
            }
            if (this.filters.search.length >= 2) {
                query.search = this.filters.search
            }
            const response = await InventoryOrderStore.dispatch('GET_HISTORY', query)
            if (response.status === 200) {
                this.orders = response.data.orders
                this.business_details = response.data.business_details
            } else if (response.status === 422) {
                this.orders = []
                this.$bus.$emit('Notification/show', { title: this.$t('notifications.validation_error'), msg: this.$t('notifications.validation_error_description'), level: 'danger' })
            } else {
                this.orders = []
                this.$bus.$emit('Notification/show', { title: this.$t('notifications.something_went_wrong'), msg: this.$t('notifications.please_retry_the_operation_or_refresh'), level: 'danger' })
            }
        }
    }
}
</script>
